import { Component, OnInit, inject, ViewChild, ElementRef, HostListener } from '@angular/core';
import { SellerService } from '../../services/seller.service';
import { CommonModule, Location, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TokenStore } from '../../services/token-store';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { SystemApp } from '../../constans/SystemConfigurations';
import { ApiUrl } from '../../shared/models/apiurl.models';
import { ContactMenuComponent } from '../../components/contact-menu/contact-menu.component';
import { BusinessContactComponent } from '../../components/business-contact/business-contact.component';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { CreditCardComponent } from '../create-adv/select-route/credit-card/credit-card.component';
import { CartService } from '../../services/cart.service';
import { TrendsService, TrendComment } from '../../services/trends.service';
import { ToastService } from '../../shared/toast/toast.service';
import { TrendCommentThreadComponent } from '../trends/trend-comment-thread.component';
import { AdvertiserContact, BusinessService } from '../../services/business.service';


interface Review {
  product_id: number;
  product_name: string;
  reviewer: string;
  reviewer_email: string;
  status: string;
  date_created: string;
  date_created_gmt: string;
  review: string;
  rating: number;
}

@Component({
    selector: 'app-product',
    imports: [CommonModule, FormsModule, RouterModule, TranslateModule, ContactMenuComponent, TrendCommentThreadComponent, BusinessContactComponent],
    templateUrl: 'product.component.html',
    styleUrls: ['product.component.scss']
})
export class ProductComponent implements OnInit {
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private location = inject(Location);

  private options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.apiKey.getJWTToken()
    })
  };

  @ViewChild('relatedTrack') relatedTrackRef!: ElementRef<HTMLDivElement>;

  product: any = null;
  relatedProducts: any[] = [];

  // Touch swipe support
  private touchStartX = 0;
  isLoading = false;
  error: string | null = null;
  activeImageIndex = 0;
  activeTab = 'desc';

  // Reviews
  reviews: TrendComment[] = [];
  reviewsLoading = false;
  averageRating = 0;
  ratingDistribution: number[] = [0, 0, 0, 0, 0]; // 1-5 stars

  // New review form
  newReview = { reviewer: '', reviewer_email: '', review: '', rating: 0 };
  hoverRating = 0;
  submittingReview = false;
  reviewSubmitted = false;
  replyTarget: { postId: number; comment: TrendComment } | null = null;
  replyText = '';
  replySubmitting = false;

  private apiUrl = SystemApp.base_url_api + '/clientAjax/product';
  private reviewsUrl = SystemApp.base_url_api + '/products/reviews';
  private insertReviewUrl = SystemApp.base_url_api + '/insert-review';

  private readonly CAR_FIELDS = ['producer', 'engineCapacity', 'year', 'fuel', 'hp', 'seats', 'fourWheelDrive', 'finishLevel', 'commercialNickname'];

  constructor(
    private sellerService: SellerService,private apiKey: ApiUrl, private translate: TranslateService, private dialog: MatDialog, public cartService: CartService,
    private trendsService: TrendsService, private toastService: ToastService) {}

  getDescription(): string {
    const value = this.product?.description;
    if (!value) return '';

    const parsed = this.tryParse(value);
    if (parsed && typeof parsed === 'object') {
      // Case 1: { he: '...', en: '...' } language map
      const hasLangKeys = ['he', 'en', 'ar', 'ru', 'fr', 'de', 'tr'].some(l => parsed[l] !== undefined);
      if (hasLangKeys) {
        const lang = this.translate.currentLang || this.translate.defaultLang || 'he';
        return parsed[lang] ?? parsed['he'] ?? parsed['en'] ?? value;
      }
      // Case 2: structured car data — render via i18n template
      if (this.CAR_FIELDS.some((f: string) => parsed[f] !== undefined)) {
        const params = Object.fromEntries(
          Object.entries(parsed).map(([k, v]) => [k, v ?? ''])
        );
        const translated = this.translate.instant('sale4you.label.licenseCodeDes.header.2', params);
        return translated.replace(/\{\{[^}]+\}\}/g, '').replace(/ {2,}/g, ' ').trim();
      }
    }

    return value;
  }

  private tryParse(value: string): any {
    // 1. Try standard JSON first
    try { return JSON.parse(value); } catch {}
    // 2. Python dict uses single quotes — convert and retry
    try {
      const jsonLike = value
        .replace(/'/g, '"')           // single → double quotes
        .replace(/True/g, 'true')     // Python booleans
        .replace(/False/g, 'false')
        .replace(/None/g, 'null');
      return JSON.parse(jsonLike);
    } catch {}
    return null;
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.loadProduct(params['id']);
      }
    });
  }

  /** SCRUM-222: the ad is not public (or does not exist). */
  notAvailable = false;

  /** Human label for a non-public ad the owner / admin is previewing. */
  get pendingStatusLabel(): string | null {
    const s = this.product?.statusAds;
    if (!this.product || (s === 'Active' && this.product.active !== false)) return null;
    const labels: {[k: string]: string} = {
      inReview: 'המודעה ממתינה לאישור מנהל',
      PayPending: 'המודעה ממתינה לתשלום',
      Blocked: 'המודעה לא אושרה',
      Pause: 'המודעה מושהית',
      Done: 'המודעה הסתיימה',
      Delete: 'המודעה נמחקה',
    };
    return labels[s] || 'המודעה אינה פעילה';
  }

  loadProduct(id: string): void {
    this.isLoading = true;
    this.error = null;
    this.notAvailable = false;
    this.http
        .get<any>(`${this.apiUrl}/${id}/`, { headers: this.options.headers })
        .pipe(catchError((err) => {
          // SCRUM-222: 404 = the ad is not public (in review, rejected, paused,
          // deleted) or does not exist.
          this.notAvailable = err?.status === 404;
          this.error = err.message;
          this.isLoading = false;
          return of(null);
        }))
        .subscribe((res) => {
          if (res) {
            this.product = res;
            this.loadReviews(res.id);
            this.loadRelatedProducts(String(id));
          }
          this.isLoading = false;
        });
  }

  loadRelatedProducts(currentId: string): void {
    const resourcetype = this.product?.resourcetype;
    if (!resourcetype) return;

    const price = Number(this.product?.price);
    const body: any = {};
    if (price && !isNaN(price) && price > 0) {
      body['min_price'] = Math.floor(price * 0.7);
      body['max_price'] = Math.ceil(price * 1.3);
    }

    const url = `${SystemApp.base_url_api}/clientAjax/productVFilteriewSet?model_class=${resourcetype}&offset=1&limit=12`;
    this.http.post<any>(url, body, { headers: this.options.headers })
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        this.relatedProducts = (res?.object_list ?? [])
          .filter((p: any) => String(p.id) !== String(currentId))
          .slice(0, 10);
      });
  }

  loadReviews(productId: number): void {
    this.reviewsLoading = true;
    this.http
        .get<any[]>(`${this.reviewsUrl}?product=${productId}`, { headers: this.options.headers })
        .pipe(catchError(() => { this.reviewsLoading = false; return of([]); }))
        .subscribe((reviews) => {
          this.reviews = reviews.map(r => ({
            id: typeof r.id === 'number' ? r.id : null,
            username: r.username || r.reviewer || 'משתמש',
            text: r.review || r.comments || '',
            timeAgo: this.formatReviewDate(r.date_created_gmt || r.date_created),
            starRating: r.rating || 0,
            parentId: r.parent_review_id ?? null,
            replyToUsername: r.reply_to_username ?? null,
            reactionCounts: r.reaction_counts || {},
            userReaction: r.user_reaction || null,
          }));
          this.calculateRatingStats();
          this.reviewsLoading = false;
        });
  }

  calculateRatingStats(): void {
    if (this.reviews.length === 0) {
      this.averageRating = 0;
      this.ratingDistribution = [0, 0, 0, 0, 0];
      return;
    }
    const ratedReviews = this.reviews.filter(r => !r.parentId && r.starRating > 0);
    const total = ratedReviews.reduce((sum, r) => sum + r.starRating, 0);
    this.averageRating = ratedReviews.length ? Math.round((total / ratedReviews.length) * 10) / 10 : 0;
    this.ratingDistribution = [0, 0, 0, 0, 0];
    ratedReviews.forEach(r => {
      if (r.starRating >= 1 && r.starRating <= 5) this.ratingDistribution[r.starRating - 1]++;
    });
  }

  getRatingPercent(starIndex: number): number {
    const ratedCount = this.reviews.filter(r => !r.parentId && r.starRating > 0).length;
    return ratedCount ? (this.ratingDistribution[starIndex] / ratedCount) * 100 : 0;
  }

  topLevelReviews(): TrendComment[] { return this.reviews.filter(review => !review.parentId); }

  startReviewReply(comment: TrendComment): void {
    if (!comment.id || !this.product?.id) return;
    this.replyTarget = { postId: this.product.id, comment };
    this.replyText = '';
  }

  cancelReviewReply(): void { this.replyTarget = null; this.replyText = ''; }

  submitReviewReply(): void {
    const target = this.replyTarget;
    const text = this.replyText.trim();
    if (!target?.comment.id || !text || this.replySubmitting) return;
    this.replySubmitting = true;
    this.trendsService.submitReply(this.product.id, text, target.comment.id).subscribe({
      next: () => { this.replySubmitting = false; this.cancelReviewReply(); this.loadReviews(this.product.id); this.toastService.success('התשובה פורסמה'); },
      error: () => { this.replySubmitting = false; this.toastService.error('לא הצלחנו לפרסם את התשובה'); }
    });
  }

  reactToReview(event: { comment: TrendComment; reaction: string }): void {
    if (!event.comment.id) return;
    this.trendsService.reactToComment(event.comment.id, event.reaction).subscribe({
      next: response => { event.comment.reactionCounts = response.reaction_counts || {}; event.comment.userReaction = response.user_reaction; },
      error: () => this.toastService.error('לא הצלחנו לשמור את התגובה')
    });
  }

  setNewRating(star: number): void { this.newReview.rating = star; }
  setHoverRating(star: number): void { this.hoverRating = star; }
  clearHoverRating(): void { this.hoverRating = 0; }

  submitReview(): void {
    if (!this.newReview.review || this.newReview.rating === 0) return;
    this.submittingReview = true;

    const user = this.product?.User;
    const cookie = JSON.stringify({
      id: user?.id || null,
      username: null,
      nicename: null,
      email: this.newReview.reviewer_email || user?.email || '',
      url: null,
      registered: null,
      displayname: null,
      first_name: this.newReview.reviewer || user?.first_name || '',
      last_name: user?.last_name || '',
      nickname: null,
      description: null,
      birthday: null,
      avatar: user?.avatar || null,
      roles: null,
      country: null
    });

    const body = {
      product_id: this.product.id,
      comments: this.newReview.review,
      cookie: cookie,
      rating: this.newReview.rating,
      image: []
    };

    this.http
        .post(`${this.insertReviewUrl}`, body, { headers: this.options.headers })
        .pipe(catchError(() => { this.submittingReview = false; return of(null); }))
        .subscribe((res) => {
          this.submittingReview = false;
          if (res) {
            this.reviewSubmitted = true;
            this.newReview = { reviewer: '', reviewer_email: '', review: '', rating: 0 };
            this.loadReviews(this.product.id);
            setTimeout(() => this.reviewSubmitted = false, 4000);
          }
        });
  }
  // Add this property
  activeMediaType: 'image' | 'video' = 'image';

// Replace setImage
  setImage(i: number): void {
    this.activeImageIndex = i;
    this.activeMediaType = 'image';
  }

// Add this method
  setVideo(): void {
    this.activeMediaType = 'video';
  }

// Replace nextImage
  nextImage(): void {
    const total = (this.product?.images?.length || 0) + (this.product?.video_url ? 1 : 0);
    if (total <= 1) return;
    if (this.activeMediaType === 'video') {
      this.activeMediaType = 'image';
      this.activeImageIndex = 0;
    } else {
      const nextIndex = this.activeImageIndex + 1;
      if (nextIndex < (this.product?.images?.length || 0)) {
        this.activeImageIndex = nextIndex;
      } else if (this.product?.video_url) {
        this.activeMediaType = 'video';
      } else {
        this.activeImageIndex = 0;
      }
    }
  }

// Replace prevImage
  prevImage(): void {
    const imgCount = this.product?.images?.length || 0;
    const hasVideo = !!this.product?.video_url;
    if (this.activeMediaType === 'video') {
      this.activeMediaType = 'image';
      this.activeImageIndex = imgCount - 1;
    } else if (this.activeImageIndex > 0) {
      this.activeImageIndex--;
    } else if (hasVideo) {
      this.activeMediaType = 'video';
    } else {
      this.activeImageIndex = imgCount - 1;
    }
  }

// Add helper
  getTotalMediaCount(): number {
    return (this.product?.images?.length || 0) + (this.product?.video_url ? 1 : 0);
  }


  onVideoError(e: Event): void {
    (e.target as HTMLVideoElement).style.display = 'none';
  }
  formatReviewDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  getReviewerInitial(name: string): string {
    return name ? name.charAt(0) : '?';
  }

  // ── Existing methods ──

  scrollRelated(dir: 'left' | 'right'): void {
    const el = this.relatedTrackRef?.nativeElement;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -220 : 220, behavior: 'smooth' });
  }

  onRelatedTouchStart(e: TouchEvent): void {
    this.touchStartX = e.changedTouches[0]?.clientX ?? 0;
  }

  onRelatedTouchEnd(e: TouchEvent): void {
    const dx = (e.changedTouches[0]?.clientX ?? 0) - this.touchStartX;
    if (Math.abs(dx) < 40) return;
    this.scrollRelated(dx < 0 ? 'right' : 'left');
  }

  goBack(): void { this.location.back(); }


  onImageError(e: Event): void {
    (e.target as HTMLImageElement).src = 'https://placehold.co/500x400/f5f5f5/999?text=📷';
  }

  formatPrice(price: number | null): string {
    if (!price) return 'ליצירת קשר';
    return '₪' + price.toLocaleString();
  }

  formatMileage(m: number | null): string {
    if (!m) return '—';
    return m.toLocaleString() + ' ק״מ';
  }

  getCategoryLabel(): string {
    // SCRUM-154: motorcycle / scooter ads are Car products — show their sub-category.
    if (this.hasCategoryDetails() && this.product?.sconderyCategory?.name) {
      return this.product.sconderyCategory.name;
    }
    const map: Record<string, string> = {
      Car: 'רכב', Pet: 'חיות מחמד', Nadlan: 'נדל״ן', Sale2: 'יד שניה',
      Required: 'דרושים', Coworker: 'בעלי מקצוע', Teacher: 'מורים פרטיים'
    };
    return map[this.product?.resourcetype] || 'מוצר';
  }

  // ── SCRUM-154 — ads published through an overloaded form (motorcycle /
  // scooter) carry their category-specific fields in `dynamicDetails`, an
  // ordered [{key, label, value, section}] list saved by the backend.
  hasCategoryDetails(): boolean {
    return Array.isArray(this.product?.dynamicDetails) && this.product.dynamicDetails.length > 0;
  }

  getCategoryDetails(): Array<{key: string; label: string; value: string; section?: string}> {
    return this.hasCategoryDetails() ? this.product.dynamicDetails : [];
  }

  getCategoryDetail(key: string): string | null {
    return this.getCategoryDetails().find(detail => detail.key === key)?.value ?? null;
  }

  getCategoryQuickSpecs(): Array<{key: string; label: string; value: string}> {
    const preferred = ['manufacturer', 'model', 'year', 'engineSize', 'mileage', 'color'];
    const details = this.getCategoryDetails();
    const picked = preferred
      .map(key => details.find(detail => detail.key === key))
      .filter((detail): detail is {key: string; label: string; value: string} => !!detail);
    // Categories without vehicle specs (e.g. accessories): the first answers.
    return (picked.length >= 2 ? picked : details).slice(0, 5);
  }

  getPhone(): string {
    // SCRUM-287 — with "פרטי העסק" on, the number shown is the one the
    // business chose. Off → unchanged.
    return this.businessContact?.phone
      || this.product?.userContactDetails?.phoneNumber || this.product?.User?.mainPhone || '';
  }

  /** SCRUM-287 — the business contact block, or null when not switched on. */
  get businessContact(): AdvertiserContact | null {
    return BusinessService.contactFor(this.product);
  }

  // ── SCRUM-87 / SCRUM-88 — seller identity + follow ─────────────────────

  sellerFollowBusy = false;
  sellerFollowError = '';
  /** Local override once the viewer follows/unfollows on this page. */
  private sellerFollowOverride: boolean | null = null;

  /** The seller's user id, or null when the payload predates it. */
  get sellerId(): number | null {
    const id = this.product?.User?.id;
    return typeof id === 'number' ? id : null;
  }

  /**
   * Hides the follow control on your own ad — you cannot follow yourself.
   *
   * Answered by the SERVER, not by the client. Nothing in the web app
   * stores the logged-in user's id — localStorage holds token, name,
   * email and avatar, and UserProfile has no id field — so a client-side
   * comparison has nothing to compare against and silently never matches.
   */
  get isOwnAd(): boolean {
    return this.product?.User?.is_self === true;
  }

  /**
   * SCRUM-88 — the blue verified tick, Facebook-style.
   *
   * Driven by is_vip_active, the entitlement RESOLVED against the
   * subscription end date — never the raw is_vip column, which the backend
   * never clears. Reading that directly would keep a tick on a seller who
   * stopped paying a year ago, and a verification mark that survives
   * non-payment is not a verification mark.
   */
  get sellerIsVerified(): boolean {
    // SCRUM-261: "Buying VIP or a promotion never grants the badge." The tick
    // now means a license checked by the site team; VIP shows as the
    // business tag below instead.
    return this.product?.User?.store?.verified === true;
  }

  /** SCRUM-261 — what the tick says was checked, e.g. "רישיון תיווך אומת". */
  get sellerVerifiedText(): string {
    return this.product?.User?.store?.verified_text || 'מוכר מאומת';
  }

  /** SCRUM-262 — the business's license number, when one was entered. */
  get sellerLicenseNumber(): string {
    return this.product?.User?.store?.license_number || '';
  }

  /** SCRUM-266 — paid placement, labelled separately from the business tag. */
  get isSponsored(): boolean {
    return (this.product as any)?.sponsored === true;
  }

  /** SCRUM-262 — "מתווך" / "סוחר רכב" / "עסק", or null for a private seller. */
  get businessTag(): string | null {
    return BusinessService.tagFor(this.product);
  }

  get isFollowingSeller(): boolean {
    if (this.sellerFollowOverride !== null) return this.sellerFollowOverride;
    return !!this.product?.User?.is_following;
  }

  /** Store name when the seller has one, their personal name otherwise. */
  get sellerHeadline(): string {
    const store = this.product?.User?.store;
    if (store && store.name) return store.name;
    return this.getSellerName();
  }

  /** The store subtitle, e.g. 'סוחר רכב מורשה'. Empty for private sellers. */
  get sellerSubtitle(): string {
    return this.product?.User?.store?.subtitle || '';
  }

  toggleSellerFollow(event: Event): void {
    // The control sits inside the routerLink to the seller page, so the
    // click must not also navigate.
    event.preventDefault();
    event.stopPropagation();

    const id = this.sellerId;
    if (!id || this.sellerFollowBusy) return;

    const was = this.isFollowingSeller;
    // Optimistic — the icon flips immediately, then reconciles.
    this.sellerFollowOverride = !was;
    this.sellerFollowBusy = true;
    this.sellerFollowError = '';

    const req = was
      ? this.sellerService.unfollow(id)
      : this.sellerService.follow(id);

    req.subscribe({
      next: (res: any) => {
        this.sellerFollowBusy = false;
        this.sellerFollowOverride = !!res.following;
      },
      error: (err: any) => {
        this.sellerFollowBusy = false;
        this.sellerFollowOverride = was;
        this.sellerFollowError =
          err?.status === 401 || err?.status === 403
            ? 'צריך להתחבר כדי לעקוב'
            : 'הפעולה נכשלה';
        setTimeout(() => (this.sellerFollowError = ''), 4000);
      },
    });
  }

  getSellerName(): string {
    return this.product?.userContactDetails?.contactPerson || this.product?.companyName || this.product?.businessName || this.product?.User?.first_name || '';
  }

  getCreatedDate(): string {
    if (!this.product?.createTime) return '—';
    return new Date(this.product.createTime).toLocaleDateString('he-IL');
  }

  getDisplayPrice(): string {
    const p = this.product;
    if (!p) return '';
    switch (p.resourcetype) {
      case 'Pet': return p.freeAdoption ? 'אימוץ חינם' : this.formatPrice(p.price);
      case 'Required': return p.salaryFrom && p.salaryTo ? `₪${parseFloat(p.salaryFrom).toLocaleString()}-₪${parseFloat(p.salaryTo).toLocaleString()} ${p.salaryType?.label || ''}` : 'ליצירת קשר';
      case 'Coworker': return p.priceFrom && p.priceTo ? `₪${parseFloat(p.priceFrom).toLocaleString()}-₪${parseFloat(p.priceTo).toLocaleString()} ${p.pricingType?.label || ''}` : 'ליצירת קשר';
      case 'Teacher': return p.pricePerHour ? `₪${parseFloat(p.pricePerHour).toLocaleString()}/שעה` : 'ליצירת קשר';
      default: return this.formatPrice(p.price);
    }
  }

  getRatingStars(rating: string | null): number {
    return rating ? Math.round(parseFloat(rating)) : 0;
  }

  // ── Market / Cart (SCRUM-20) ─────────────────────────────────────────────

  cartLoading = false;
  cartError: string | null = null;
  cartAdded = false;

  isMarketProduct(): boolean {
    return this.product?.is_market_product === true;
  }

  isOutOfStock(): boolean {
    return this.isMarketProduct() && (this.product?.stock_qty != null) && Number(this.product.stock_qty) <= 0;
  }

  getMarketPrice(): string {
    const mp = this.product?.market_price;
    if (!mp) return '';
    return '₪' + Number(mp).toLocaleString();
  }

  /** "הוסף לסל" — adds to local cart, shows feedback */
  addToCart(): void {
    if (!this.product || this.isOutOfStock()) return;
    this.cartService.add({
      id:    this.product.id,
      name:  this.product.name,
      price: Number(this.product.market_price ?? this.product.price),
      image: this.product.images?.[0]?.image ?? null,
    });
    this.cartAdded = true;
    setTimeout(() => { this.cartAdded = false; }, 2500);
  }

  /** "קנה עכשיו" — bypasses cart, opens Z-Credit immediately */
  buyNow(): void {
    if (!this.product || this.isOutOfStock() || this.cartLoading) return;

    // /auth/login does not exist - open the login modal on this page.
    if (!TokenStore.isLoggedIn()) {
      window.location.href = window.location.pathname + '?loginOpenForm=true';
      return;
    }

    this.cartLoading = true;
    this.cartError = null;

    // Bearer is added by AppInterceptor. This used to send 'Token ' + the
    // is_logged_in marker, i.e. the literal header 'Token 1'.
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    this.http.post<{ session_id: string; session_url: string; order_id: number; amount: number }>(
      this.apiKey.initiateMarketPurchase(),
      { product_id: this.product.id, quantity: 1 },
      { headers }
    ).pipe(catchError(err => {
      this.cartLoading = false;
      this.cartError = err?.error?.error || 'שגיאה ביצירת הזמנה';
      return of(null);
    })).subscribe(res => {
      this.cartLoading = false;
      if (!res) return;
      this.dialog.open(CreditCardComponent, {
        data: {
          adsRoute: {
            id: this.product.id, title: this.product.name,
            price: res.amount, lifeSpan: null, image: '', level: null,
          },
          adsId: null, isSubscription: false,
          isMarketPurchase: true, orderId: res.order_id, sessionId: res.session_id,
        },
        width: '760px', direction: 'rtl', panelClass: 'market-payment-dialog',
      });
    });
  }

  // ── SCRUM-135 — "פרטים נוספים" / "מאפיינים" tabs ───────────────────────
  // Rows / chips are built from the API response and filtered to the ones
  // that carry a real value, so a blank value never gets a label and an
  // empty tab shows its empty-state text instead of a bare table.

  private detailsCache: { product: any; rows: DetailRow[] } | null = null;
  private featuresCache: { product: any; chips: FeatureChip[] } | null = null;

  /** True when `v` is real data (not null/undefined/blank/placeholder/NaN). */
  private hasValue(v: any): boolean {
    if (v === null || v === undefined) return false;
    if (typeof v === 'number') return !isNaN(v);
    if (typeof v === 'boolean') return v;
    if (Array.isArray(v)) return v.some(x => this.hasValue(x));
    const str = String(v).trim();
    return str !== '' && str !== '—' && str !== '-' && str !== 'null' && str !== 'undefined';
  }

  /** Pushes a row only when every required part has a real value. */
  private pushRow(rows: DetailRow[], label: string, value: any, ...required: any[]): void {
    const parts = required.length ? required : [value];
    if (!parts.every(p => this.hasValue(p))) return;
    const text = String(value ?? '').replace(/\s{2,}/g, ' ').trim();
    if (text) rows.push({ label, value: text });
  }

  /** "₪from-₪to suffix" with whichever end exists; '' when neither does. */
  private priceRange(from: any, to: any, suffix?: any): string {
    const f = this.hasValue(from) ? `₪${from}` : '';
    const t = this.hasValue(to) ? `₪${to}` : '';
    if (!f && !t) return '';
    const range = f && t ? `${f}-${t}` : (f || t);
    return this.hasValue(suffix) ? `${range} ${suffix}` : range;
  }

  detailRows(): DetailRow[] {
    const p = this.product;
    if (!p) return [];
    if (this.detailsCache?.product === p) return this.detailsCache.rows;

    const rows: DetailRow[] = [];
    const add = (label: string, value: any, ...required: any[]) => this.pushRow(rows, label, value, ...required);

    switch (p.resourcetype) {
      case 'Car':
        if (this.hasCategoryDetails()) {
          // SCRUM-154: motorcycle / scooter — exactly the fields of the selected category
          for (const d of this.getCategoryDetails()) add(d.label, d.value);
          break;
        }
        add('יצרן', p.CodeProducer);
        add('דגם', p.MainModel);
        add('שנה', p.year);
        add('ק״מ', p.mileage ? this.formatMileage(p.mileage) : '', p.mileage);
        add('נפח מנוע', `${p.engineCapacity} cc`, p.engineCapacity);
        add('צבע', p.Color?.colorName);
        add('בעלות', p.NumberOfOwner?.numberOnwer);
        add('בעלים נוכחי', p.CurrentOwner?.Type);
        add('בעלים קודם', p.PastOwner?.Type);
        add('מספר רישוי', p.licenseNumber);
        add('טסט', `${p.MonthTest ?? ''} ${p.YearTest}`, p.YearTest);
        break;
      case 'Nadlan':
        add('סוג נכס', p.TypeOfNadlan?.nameHe);
        add('מצב', p.StatusOfNadlan?.nameHe);
        add('עיר', p.City?.nameHe);
        add('רחוב', p.Street?.nameHe);
        add('שכונה', p.Neighborhood);
        add('חדרים', p.RoomsNum?.numberOfRooms);
        add('קומה', `${p.Floor}${this.hasValue(p.TotalFloor) ? ' / ' + p.TotalFloor : ''}`, p.Floor);
        add('שטח', `${p.buildingMeterTotal} מ״ר`, p.buildingMeterTotal);
        add('חניה', p.ParkingNumber?.numberParking);
        add('ועד בית', `₪${p.condo}`, p.condo || '');
        add('ארנונה', `₪${p.arnona}`, p.arnona || '');
        break;
      case 'Pet':
        add('סוג', p.petType?.label);
        add('גזע', p.breed?.label);
        add('שם', p.petName);
        add('גיל', p.age?.label);
        add('מין', p.gender?.label);
        add('גודל', p.size?.label);
        add('צבע', p.color);
        add('עיר', p.city?.nameHe);
        add('אזור', p.region?.label);
        break;
      case 'Sale2':
        add('קטגוריה', p.sconderyCategory?.name);
        add('תת קטגוריה', p.subCategory?.subCategoryName);
        add('מצב', p.ProductStatus?.statusName);
        add('עיר', p.City?.nameHe);
        break;
      case 'Required':
        add('תפקיד', p.jobTitle);
        add('תחום', p.jobCategory?.label);
        add('סוג משרה', p.jobType?.label);
        add('מיקום עבודה', p.workLocationType?.label);
        add('חברה', p.companyName);
        add('עיר', p.city?.nameHe);
        add('אזור', p.region?.label);
        add('שכר', this.priceRange(p.salaryFrom, p.salaryTo, p.salaryType?.label));
        break;
      case 'Coworker':
        add('מקצוע', p.professionalTitle);
        add('תחום', p.professionCategory?.label);
        add('סוג שירות', p.serviceType?.label);
        add('ניסיון', p.experienceYears?.label);
        add('עיר', p.city?.nameHe);
        add('עסק', p.businessName);
        add('מחיר', this.priceRange(p.priceFrom, p.priceTo, p.pricingType?.label));
        break;
      case 'Teacher':
        add('כותרת', p.teacherTitle);
        add('השכלה', p.teacherEducation?.label);
        add('ניסיון', p.experienceYears?.label);
        add('מחיר לשעה', `₪${p.pricePerHour}`, p.pricePerHour);
        add('חבילה', `₪${p.pricePerPackage}${this.hasValue(p.packageSize) ? ` (${p.packageSize} שיעורים)` : ''}`, p.pricePerPackage);
        add('עיר', p.city?.nameHe);
        add('אזור', p.region?.label);
        add('עסק', p.businessName);
        break;
    }

    this.detailsCache = { product: p, rows };
    return rows;
  }

  featureChips(): FeatureChip[] {
    const p = this.product;
    if (!p) return [];
    if (this.featuresCache?.product === p) return this.featuresCache.chips;

    const chips: FeatureChip[] = [];
    const flag = (on: any, text: string) => { if (on === true || on === 'true' || on === 1) chips.push({ text, accent: false }); };
    const list = (items: any, pick: (x: any) => any, accent: boolean) => {
      if (!Array.isArray(items)) return;
      for (const it of items) {
        const t = pick(it);
        if (this.hasValue(t)) chips.push({ text: String(t).trim(), accent });
      }
    };

    switch (p.resourcetype) {
      case 'Car':
        if (this.hasCategoryDetails()) {
          if (p.DrivingTest) {
            chips.push({ text: 'ללא טסט', accent: false });
          } else {
            const until = this.getCategoryDetail('testUntil');
            if (this.hasValue(until)) chips.push({ text: `✓ טסט עד ${this.formatMonthYear(until!)}`, accent: false });
          }
          break;
        }
        flag(p.DrivingTest, '✓ טסט בתוקף');
        flag(p.exchange, '🔄 חליפין');
        flag(p.DisabledSuitable, '♿ מונגש');
        list(p.armatureList, a => a?.ArmatureDesc, true);
        break;
      case 'Nadlan':
        flag(p.OnColumns, '🏗️ על עמודים');
        flag(p.flexible, '🔄 גמיש');
        flag(p.availabilityEntrance, '⚡ כניסה מיידית');
        flag(p.longTime, '📅 טווח ארוך');
        list(p.armatureList, a => a?.ArmatureDesc, true);
        break;
      case 'Pet':
        list(p.suitability, s => (typeof s === 'object' ? s?.label : s), false);
        break;
      case 'Sale2':
        flag(p.DeliveryOffer, '🚚 כולל משלוח');
        break;
      case 'Required':
        flag(p.isNegotiable, '💬 שכר גמיש');
        break;
      case 'Coworker':
        flag(p.freeEstimate, '🆓 הערכה חינם');
        break;
      case 'Teacher':
        list(p.teachingSubjects, s => s?.label, true);
        flag(p.freeTrialLesson, '🎁 שיעור ניסיון חינם');
        flag(p.priceNegotiable, '💬 מחיר גמיש');
        flag(p.groupLesson, '👥 שיעור קבוצתי');
        break;
    }

    this.featuresCache = { product: p, chips };
    return chips;
  }

  hasFeatures(): boolean {
    return this.featureChips().length > 0;
  }

  private formatMonthYear(value: string): string {
    try {
      return formatDate(value, 'MM/yyyy', 'en-US');
    } catch {
      return value;
    }
  }

  // ── SCRUM-133 — share menu ─────────────────────────────────────────────

  shareOpen = false;

  toggleShare(event: Event): void {
    event.stopPropagation();
    this.shareOpen = !this.shareOpen;
  }

  @HostListener('document:click')
  onDocumentClickCloseShare(): void {
    this.shareOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscapeCloseShare(): void {
    this.shareOpen = false;
  }

  private shareUrl(): string {
    if (typeof window === 'undefined') return '';
    return window.location.href.split('#')[0];
  }

  private openShareWindow(url: string): void {
    if (typeof window === 'undefined') return;
    window.open(url, '_blank', 'noopener,noreferrer');
    this.shareOpen = false;
  }

  shareWhatsApp(): void {
    const text = `${this.product?.name ?? ''} ${this.shareUrl()}`.trim();
    this.openShareWindow(`https://wa.me/?text=${encodeURIComponent(text)}`);
  }

  shareFacebook(): void {
    this.openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.shareUrl())}`);
  }

  /**
   * Instagram has no web share URL. On devices with the Web Share API
   * (mobile) open the native share sheet — Instagram is offered there;
   * elsewhere copy the link so the user can paste it into Instagram.
   */
  async shareInstagram(): Promise<void> {
    const url = this.shareUrl();
    const nav: any = typeof navigator !== 'undefined' ? navigator : null;
    if (nav?.share) {
      try {
        await nav.share({ title: this.product?.name ?? '', text: this.product?.name ?? '', url });
        this.shareOpen = false;
        return;
      } catch (err: any) {
        if (err?.name === 'AbortError') { this.shareOpen = false; return; }
        // fall through to copy
      }
    }
    await this.copyShareLink('הקישור הועתק — אפשר להדביק אותו באינסטגרם');
  }

  async copyShareLink(message = 'הקישור הועתק'): Promise<void> {
    const url = this.shareUrl();
    this.shareOpen = false;
    if (!url) return;
    let ok = false;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        ok = true;
      }
    } catch { /* fall back below */ }
    if (!ok && typeof document !== 'undefined') {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { ok = document.execCommand('copy'); } catch { ok = false; }
      document.body.removeChild(ta);
    }
    if (ok) {
      this.toastService.success(message);
    } else {
      this.toastService.error('לא ניתן היה להעתיק את הקישור');
    }
  }
}

interface DetailRow { label: string; value: string; }
interface FeatureChip { text: string; accent: boolean; }
